import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { distinctUntilChanged, firstValueFrom, map, Observable } from 'rxjs';

import { AccessConfigEntity } from '../../domain/exposition-access-config.entity';
import { ExpositionColumnConfigListEntity } from '../../domain/exposition-column-config-list.entity';
import { ExpositionConsumerListEntity } from '../../domain/exposition-consumer-list.entity';
import { ExpositionEndpointMetadataEntity } from '../exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
import { ExpositionMetadataEntity } from '../exposition-internal-metadata-config/exposition-metadata.entity';

export enum ExpositionStep {
	Datablock = 0,
	Metadata = 1,
	ColumnsSelection = 2,
	ColumnsConfig = 3,
	Access = 4,
}

export interface IExpositionStepperValidity {
	step1: boolean;
	step2: boolean;
	step3: boolean;
	step4: boolean;
	step5: boolean;
}

interface IExpositionStepperState {
	currentStep: ExpositionStep;
	previousStep: ExpositionStep;
	selectedDatablockId: number;
	hasDatablockSelectionChanged: boolean;
	expositionMetadata: ExpositionMetadataEntity;
	endpointMetadata: ExpositionEndpointMetadataEntity;
	columnsSelection: ExpositionColumnConfigListEntity;
	accessConfig: AccessConfigEntity;
	stepsValidity: IExpositionStepperValidity;
}

@Injectable()
export class ExpositionStore extends ComponentStore<IExpositionStepperState> {
	public readonly selectedDatablockId$ = this.state.pipe(
		map((state) => state.selectedDatablockId)
	);
	public readonly hasDatablockSelectionChanged$ = this.state.pipe(
		map((state) => state.hasDatablockSelectionChanged)
	);
	public readonly currentStep$ = this.state.pipe(
		map((state) => state.currentStep),
		distinctUntilChanged()
	);
	public readonly previousStep$ = this.state.pipe(
		map((state) => state.previousStep)
	);

	public readonly metadata$ = this.state.pipe(
		map((state) => state.expositionMetadata)
	);

	public readonly endpointMetadata$ = this.state.pipe(
		map((state) => state.endpointMetadata)
	);

	public readonly stepsValidity$ = this.state.pipe(
		map((state) => state.stepsValidity)
	);
	public readonly isCurrentStepValid$ = this.state.pipe(
		map((state) => {
			if (state.currentStep === 0) {
				return state.stepsValidity.step1;
			}
			if (state.currentStep === 1) {
				return state.stepsValidity.step2;
			}
			if (state.currentStep === 2) {
				return state.stepsValidity.step3;
			}
			if (state.currentStep === 3) {
				return state.stepsValidity.step4;
			}
			if (state.currentStep === 4) {
				return state.stepsValidity.step5;
			}
			return false;
		})
	);

	public readonly columnsSelection$ = this.state.pipe(
		map((state) => state.columnsSelection),
		distinctUntilChanged()
	);

	public readonly columnsConfiguration$ = this.select(
		this.columnsSelection$,
		(list) => {
			const selectedColumns = list.elements.filter((col) => col.isActive);
			return list.setElements(selectedColumns);
		}
	);

	public readonly accessConfig$ = this.state.pipe(
		map((state) => state.accessConfig),
		distinctUntilChanged()
	);

	public constructor() {
		super({
			currentStep: ExpositionStep.Datablock,
			previousStep: ExpositionStep.Datablock,
			selectedDatablockId: -1,
			hasDatablockSelectionChanged: false,
			expositionMetadata: ExpositionMetadataEntity.build({}),
			endpointMetadata: ExpositionEndpointMetadataEntity.build({}),
			accessConfig: AccessConfigEntity.build({
				consumers: ExpositionConsumerListEntity.build([
					{
						field: 'avatar',
						alias: '',
						type: '',
						isSearchable: false,
						visibleIndex: 0,
					},
					{
						field: 'name',
						alias: $localize`:@@expositions.access.config.userOrGroupName:`,
						type: 'string',
						isSearchable: true,
						visibleIndex: 1,
					},
				]),
			}),
			columnsSelection: ExpositionColumnConfigListEntity.build([]),
			stepsValidity: {
				step1: false,
				step2: false,
				step3: false,
				step4: false,
				step5: false,
			},
		});
	}
	private get state(): Observable<IExpositionStepperState> {
		return this.select((state) => state);
	}

	public changeStep(steps: {
		currentStep: number;
		previousStep: number;
	}): void {
		this.patchState({
			previousStep: steps.previousStep,
			currentStep: steps.currentStep,
		});
	}

	public setDatablockId(
		selectedDatablockId: number,
		selectionChanged: boolean,
		stepsValidity: IExpositionStepperValidity
	): void {
		this.patchState({
			selectedDatablockId,
			hasDatablockSelectionChanged: selectionChanged,
			stepsValidity,
		});
	}

	public startOnSecondStep(datablockId: number, datablockLabel: string): void {
		this.setState({
			endpointMetadata: ExpositionEndpointMetadataEntity.build({}),
			accessConfig: AccessConfigEntity.build({
				consumers: ExpositionConsumerListEntity.build([
					{
						field: 'avatar',
						alias: '',
						type: '',
						isSearchable: false,
						visibleIndex: 0,
					},
					{
						field: 'name',
						alias: $localize`:@@expositions.access.config.userOrGroupName:`,
						type: 'string',
						isSearchable: true,
						visibleIndex: 1,
					},
				]),
			}),
			columnsSelection: ExpositionColumnConfigListEntity.build([]),
			currentStep: 1,
			previousStep: 0,
			hasDatablockSelectionChanged: true,
			selectedDatablockId: datablockId,
			expositionMetadata: ExpositionMetadataEntity.build({
				id: datablockId,
			}).updateLabel(datablockLabel),
			stepsValidity: {
				step1: true,
				step2: false,
				step3: false,
				step4: false,
				step5: false,
			},
		});
	}

	public async setValidity(isValid: boolean): Promise<void> {
		const currentState = await firstValueFrom(this.state);
		let { stepsValidity } = currentState;
		switch (currentState.currentStep) {
			case ExpositionStep.Datablock:
				stepsValidity = {
					...currentState.stepsValidity,
					step1: isValid,
					step3: false,
					step4: false,
					step5: false,
				};
				break;
			case ExpositionStep.Metadata:
				stepsValidity = {
					...currentState.stepsValidity,
					step2: isValid,
				};
				break;
			case ExpositionStep.ColumnsSelection:
				stepsValidity = {
					...currentState.stepsValidity,
					step3: isValid,
				};
				break;
			case ExpositionStep.ColumnsConfig:
				stepsValidity = {
					...currentState.stepsValidity,
					step4: isValid,
				};
				break;
			case ExpositionStep.Access:
				stepsValidity = {
					...currentState.stepsValidity,
					step5: isValid,
				};
		}
		this.patchState({
			stepsValidity,
		});
	}

	public setColumnsSelection(
		columnsSelection: ExpositionColumnConfigListEntity
	): void {
		this.patchState({
			columnsSelection,
		});
	}

	public setAccessConfig(accessConfig: AccessConfigEntity): void {
		this.patchState({
			accessConfig,
		});
	}

	public setExpositionInternalMetadata(
		expositionMetadata: ExpositionMetadataEntity
	): void {
		this.patchState({
			expositionMetadata,
		});
	}

	public setMetadata(
		expositionMetadata: ExpositionMetadataEntity,
		endpointMetadata: ExpositionEndpointMetadataEntity
	): void {
		this.patchState({
			expositionMetadata,
			endpointMetadata,
		});
	}
}
